import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import Download from './icon/download'
import { useQuery } from '@tanstack/react-query'
import { getEntryById } from '~/lib/contensis/misc'

const button = cva(
  'button unstyled-link gap-xs py-xs-plus pl-xs-plus pr-s group relative flex shadow-none',
  {
    variants: {
      colour: {
        'ink-blue': 'bg-blue-d text-white! antialiased',
        'mid-blue': 'bg-blue-m text-white! antialiased',
        chartreuse: 'bg-char text-blue-d!',
        white: 'bg-white text-blue-d! '
      }
    },
    defaultVariants: {
      colour: 'ink-blue'
    }
  }
)

type DownloadButton = Button & {
  data: {
    internalFile: {
      sys: {
        id: string
        uri: string | undefined
        contentTypeId: string | undefined
        properties: {
          fileSize: number
        }
      }
    }
    fileSize?: number
  }
}

function returnFileSize(number: number) {
  if (number < 1e3) {
    return `${number} bytes`
  } else if (number >= 1e3 && number < 1e6) {
    return `${(number / 1e3).toFixed(1)}KB`
  } else {
    return `${(number / 1e6).toFixed(1)}MB`
  }
}

const DownloadButton = ({ data, colour, className }: DownloadButton) => {
  const { data: file } = useQuery({
    queryKey: ['file-size', data?.internalFile?.sys?.id],
    queryFn: async () => {
      return getEntryById(data?.internalFile?.sys?.id)
    },
    enabled: !!data?.internalFile?.sys?.id
  })

  const btnColour = data?.strongColour
    ? colour === 'ink-blue'
      ? 'mid-blue'
      : 'chartreuse'
    : colour

  const fileSize = returnFileSize(file?.sys?.properties?.fileSize ?? 0)

  return (
    <>
      {!data?.disabled ? (
        <a
          href={data?.internalFile?.sys.uri}
          {...(data?.ariaLabel !== '' ? { 'aria-label': data?.ariaLabel } : {})}
          className={cn(button({ colour: btnColour }), className)}
          target='_blank'
          rel='noreferrer nofollow '
        >
          <Download
            fill={''}
            width={'3rem'}
            height={'3rem'}
            title={''}
            className='group-hover:translate-y-3xs group-focus:translate-y-3xs transition-transform duration-200'
          />
          <div className='flex grow flex-col justify-between'>
            {data?.label}
            <div className='gap-2xs text-tag-sm flex font-normal'>
              <span>{data?.internalFile?.sys.contentTypeId}</span>|
              <span>{fileSize}</span>
            </div>
          </div>
        </a>
      ) : (
        <div
          className='button unstyled-link group gap-xs bg-grey-m p-xs-plus text-blue-d relative flex cursor-not-allowed shadow-none'
          aria-disabled
        >
          <Download fill={''} width={'3rem'} height={'3rem'} title={''} />
          <div className='flex grow flex-col justify-between'>
            {data?.label}
            <div className='gap-2xs text-tag-sm flex font-normal'>
              <span>{data?.internalFile?.sys.contentTypeId}</span>|
              <span>{fileSize}</span>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DownloadButton
